import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Lock, LogOut, Users, Building2, User } from "lucide-react";
import { API } from "../lib/api";

export default function Admin() {
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/waitlist`, {
        headers: { "x-admin-token": token },
      });
      setData(res.data);
    } catch (err) {
      setError("Invalid admin token. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setData(null);
    setToken("");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-4">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <form
          onSubmit={load}
          className="relative z-10 w-full max-w-sm rounded-2xl border border-white/10 bg-void/80 p-8 backdrop-blur-sm"
          data-testid="admin-login-form"
        >
          <Link to="/" className="mb-6 flex items-center gap-2.5">
            <img src="/brand/smritium-mark-white.png" alt="" className="h-8 w-8" />
            <span className="font-display text-2xl lowercase text-paper">smritium</span>
          </Link>
          <div className="flex items-center gap-2 text-ember">
            <Lock size={16} />
            <p className="overline">Waitlist admin</p>
          </div>
          <h1 className="mt-3 font-display text-2xl text-paper">Sign in to view signups</h1>
          <input
            type="password"
            placeholder="Admin token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="mt-6 w-full rounded-lg border border-white/20 bg-ink px-4 py-3 text-sm text-paper outline-none transition-colors placeholder:text-mute/70 focus:border-ember"
            data-testid="admin-token-input"
          />
          {error && (
            <p className="mt-3 text-sm text-ember" data-testid="admin-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary mt-5 w-full disabled:opacity-60"
            data-testid="admin-login-button"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "View waitlist"}
          </button>
        </form>
      </div>
    );
  }

  const { counts, entries } = data;

  return (
    <div className="min-h-screen bg-ink">
      <div className="container-x py-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/brand/smritium-mark-white.png" alt="" className="h-8 w-8" />
            <span className="font-display text-2xl lowercase text-paper">smritium</span>
            <span className="ml-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] uppercase tracking-overline text-mute">
              Waitlist admin
            </span>
          </div>
          <button
            onClick={logout}
            className="btn-ghost !px-5 !py-2.5 text-[13px]"
            data-testid="admin-logout-button"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Users, t: "Total signups", v: counts.total },
            { icon: Building2, t: "Enterprise", v: counts.enterprise },
            { icon: User, t: "Individual", v: counts.individual },
          ].map((c) => (
            <div key={c.t} className="card">
              <c.icon className="text-ember" size={22} />
              <p className="mt-4 font-display text-4xl text-paper">{c.v}</p>
              <p className="mt-1 text-sm text-mute">{c.t}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-left text-sm" data-testid="admin-table">
            <thead className="bg-white/5 text-mute">
              <tr>
                <th className="px-5 py-4 font-medium">Name</th>
                <th className="px-5 py-4 font-medium">Email</th>
                <th className="px-5 py-4 font-medium">Organisation</th>
                <th className="px-5 py-4 font-medium">Type</th>
                <th className="px-5 py-4 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-mute">
                    No signups yet.
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-white/5">
                  <td className="px-5 py-4 text-paper">{e.name}</td>
                  <td className="px-5 py-4 text-mute">{e.email}</td>
                  <td className="px-5 py-4 text-mute">{e.organization || "—"}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] ${
                        e.persona === "enterprise"
                          ? "bg-ember/15 text-ember"
                          : "bg-white/10 text-mute"
                      }`}
                    >
                      {e.persona}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-mute">{fmt(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
